import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import KitchenLayout from '../Navigation/KitchenLayout';
import LegalLinks from '../Legal/LegalLinks';
import BillingPanel from './BillingPanel';

const AccountSettings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { logout, token, impersonation, exitImpersonation } = useAuth();
  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab === 'security' || requestedTab === 'account' ? requestedTab : 'billing';
  const tabs = [
    { id: 'billing', label: 'Billing', description: 'Plan, subscription, and usage' },
    { id: 'security', label: 'Security', description: 'Password and sign-in protection' },
    { id: 'account', label: 'Account', description: 'Account access and deletion' }
  ] as const;

  const selectTab = (tab: typeof tabs[number]['id']) => {
    setError('');
    setSuccess('');
    const nextParams = new URLSearchParams(searchParams);
    if (tab === 'billing') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tab);
    }
    setSearchParams(nextParams);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to change password');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setError('Please type DELETE to confirm account deletion');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Make sure we don't have double slashes in the URL
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const deleteUrl = `${baseUrl}/auth/delete-account`;

      const response = await fetch(deleteUrl, {
        method: 'POST', // Changed from DELETE to POST
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          password: deletePassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete account');
      }

      // Log out the user and redirect to home page
      if (impersonation) {
        await exitImpersonation();
      } else {
        logout();
        navigate('/', { state: { message: 'Your account has been successfully deleted' } });
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to delete account');
      }
      setIsSubmitting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <KitchenLayout title="Account settings">
      <div className="min-w-0 flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <header className="border-b border-gray-200 bg-white px-4 pt-5 sm:px-6 sm:pt-7">
            <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
            <p className="mt-1 text-sm text-gray-500">Manage your Kitchen account, subscription, and security.</p>

            <nav className="mt-6 flex gap-6 overflow-x-auto" aria-label="Account settings sections" role="tablist">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    id={`${tab.id}-tab`}
                    aria-selected={isActive}
                    aria-controls={`${tab.id}-panel`}
                    onClick={() => selectTab(tab.id)}
                    className={`whitespace-nowrap border-b-2 pb-3 text-sm font-medium transition-colors ${isActive ? 'border-gray-900 text-gray-950' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800'}`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </header>

          <div className="p-4 sm:p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">{tabs.find((tab) => tab.id === activeTab)?.label}</h2>
              <p className="mt-1 text-sm text-gray-500">{tabs.find((tab) => tab.id === activeTab)?.description}</p>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-6">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            {success && (
              <div className="rounded-md bg-green-50 p-4 mb-6">
                <div className="text-sm text-green-700">{success}</div>
              </div>
            )}

            {activeTab === 'billing' && (
              <div id="billing-panel" role="tabpanel" aria-labelledby="billing-tab">
                <BillingPanel />
              </div>
            )}

          {activeTab === 'security' && <div id="security-panel" role="tabpanel" aria-labelledby="security-tab" className="mx-auto max-w-xl overflow-hidden rounded-xl bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <h2 className="text-lg leading-6 font-medium text-gray-900">Change Password</h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">Update your account password</p>
            </div>
            <div className="border-t border-gray-200">
              <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6 space-y-6">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>}

          {activeTab === 'account' && <div id="account-panel" role="tabpanel" aria-labelledby="account-tab" className="mx-auto max-w-xl overflow-hidden rounded-xl bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <h2 className="text-lg leading-6 font-medium text-gray-900">Delete Account</h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">Close your account and request deletion of associated data</p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              <div className="text-sm text-gray-700 mb-4">
                <p>This action cannot be undone. Kitchen will deactivate your recipes, remove runtime files, and suspend account access. Some security, billing, communication, and audit records may be retained as described in the Privacy Policy.</p>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>}
            <LegalLinks className="mt-8 pb-4 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex min-h-[100dvh] items-end justify-center px-3 pt-4 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => !isSubmitting && setShowDeleteModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block w-full max-w-lg transform overflow-hidden rounded-t-2xl bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:rounded-lg sm:p-6 sm:align-middle">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Delete Account
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to close your account? Your recipes will stop and you will lose access.
                      Some records may be retained as described in the Privacy Policy. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-6">
                <div className="mb-4">
                  <label htmlFor="deletePassword" className="block text-sm font-medium text-gray-700 text-left">
                    Enter your password to confirm
                  </label>
                  <input
                    type="password"
                    id="deletePassword"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="deleteConfirm" className="block text-sm font-medium text-gray-700 text-left">
                    Type DELETE to confirm
                  </label>
                  <input
                    type="text"
                    id="deleteConfirm"
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    disabled={isSubmitting}
                    placeholder="DELETE"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm disabled:bg-red-400 disabled:cursor-not-allowed"
                    onClick={handleDeleteAccount}
                    disabled={isSubmitting || deleteConfirmText !== 'DELETE' || !deletePassword}
                  >
                    {isSubmitting ? 'Deleting...' : 'Delete Account'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </KitchenLayout>
  );
};

export default AccountSettings;
